<template>
  <div>
    <!-- 1. Page Header -->
    <h1 class="text-3xl font-bold mb-6 text-center text-gray-800">3. Upload Answer Pages</h1>
    <div class="flex justify-center space-x-4 mb-8">
      <button @click="goToText" class="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition">Use Text Instead</button>
      <button @click="goToQR" class="bg-indigo-500 text-white px-4 py-2 rounded hover:bg-indigo-600 transition">Scan with Phone</button>
    </div>

    <!-- 2. Modern Uploader -->
    <div class="mb-8">
      <label
          class="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg cursor-pointer transition-colors"
          :class="isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="handleFileDrop"
      >
        <div class="flex flex-col items-center justify-center">
          <svg class="w-10 h-10 mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-4-4V6a4 4 0 014-4h10a4 4 0 014 4v6a4 4 0 01-4 4H7z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 9v6m3-3H7"></path></svg>
          <p class="mb-2 text-sm text-gray-500"><span class="font-semibold">Click or drop files to upload</span></p>
          <p class="text-xs text-gray-500">PNG, JPG or PDF</p>
        </div>
        <input ref="fileInput" type="file" class="hidden" @change="handleFileSelect" multiple accept="image/png, image/jpeg, application/pdf" />
      </label>
      <div v-if="uploading" class="text-center mt-2 text-sm text-gray-500">Uploading {{ uploadProgress }}...</div>
    </div>

    <!-- 3. Page Overview Grid -->
    <div>
      <h2 class="text-xl font-semibold mb-4 text-gray-700">Uploaded Answer Key Pages</h2>
      <div v-if="pages.length === 0" class="text-center py-4 text-gray-500">
        No answer key pages have been uploaded yet.
      </div>
      <div v-else class="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-4">
        <div v-for="page in pages" :key="page.id" class="relative border rounded-lg overflow-hidden group">
          <button @click="viewPage(page.id)" class="w-full h-full">
            <img v-if="thumbnailUrls[page.id]" :src="thumbnailUrls[page.id]" class="w-full h-full object-cover" />
            <div v-else class="w-full h-24 flex items-center justify-center bg-gray-100">
              <svg class="animate-spin h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V4a10 10 0 00-10 10h2z"></path></svg>
            </div>
          </button>
          <button @click="deletePage(page.id)" class="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 transition-opacity">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      </div>
    </div>

    <!-- 4. Navigation Buttons -->
    <div class="flex justify-between mt-8">
      <button
          @click="goBack"
          class="bg-gray-500 text-white px-4 py-2 rounded transition hover:bg-gray-600 flex items-center min-w-[120px] justify-center"
          :disabled="navLoading"
      >
        Back
      </button>
      <button
          @click="triggerNext"
          class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition flex items-center min-w-[120px] justify-center"
          :disabled="navLoading">
        <svg v-if="navLoading" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V4a10 10 0 00-10 10h2zm0 0a8 8 0 018 8v-2a6 6 0 00-6-6H4z"></path>
        </svg>
        <span v-if="navLoading">Processing...</span>
        <span v-else>Generate Instructions</span>
      </button>
    </div>

    <!-- 5. Image Viewer Modal -->
    <transition name="fade">
      <div v-if="modalImageUrl" @click="modalImageUrl = null" class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <img :src="modalImageUrl" @click.stop class="max-w-full max-h-full object-contain" />
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from '../services/axiosInstance'
import SockJS from 'sockjs-client'
import Stomp from 'stompjs'
import { useModalStore } from '@/stores/modal'

const pages = ref([])
const thumbnailUrls = ref({})
const isDragging = ref(false)
const fileInput = ref(null)
const uploading = ref(false)
const uploadProgress = ref('')
const navLoading = ref(false)
const modalImageUrl = ref(null)
const stompClient = ref(null)
const router = useRouter()
const route = useRoute()
const modal = useModalStore()

// --- DATA FETCHING ---
const fetchPages = async () => {
  const testId = route.params.testId
  try {
    const response = await axios.get(`/tests/${testId}/answers/pages`)
    pages.value = response.data || []
    await fetchThumbnails()
  } catch (e) { console.error('Error fetching answer pages:', e) }
}

const fetchThumbnails = async () => {
  const testId = route.params.testId
  const promises = pages.value.map(async (page) => {
    try {
      const url = `/tests/${testId}/answers/pages/${page.id}/thumbnail`
      const response = await axios.get(url, { responseType: 'blob' })
      thumbnailUrls.value[page.id] = URL.createObjectURL(response.data)
    } catch (error) { console.error(`Error fetching thumbnail for page ${page.id}:`, error) }
  })
  await Promise.all(promises)
}

// --- USER ACTIONS ---
const deletePage = async (pageId) => {
  const confirmed = await modal.confirm('Are you sure you want to delete this answer page?', 'Delete Page', 'Delete')
  if (confirmed) {
    const testId = route.params.testId
    try {
      await axios.delete(`/tests/${testId}/answers/pages/${pageId}`)
      pages.value = pages.value.filter(p => p.id !== pageId)
      if (thumbnailUrls.value[pageId]) {
        URL.revokeObjectURL(thumbnailUrls.value[pageId])
        delete thumbnailUrls.value[pageId]
      }
    } catch (error) {
      console.error('Error deleting page:', error)
      modal.error('Failed to delete page. Please try again.')
    }
  }
}

const viewPage = async (pageId) => {
  const testId = route.params.testId
  try {
    const url = `/tests/${testId}/answers/pages/${pageId}/file`
    const response = await axios.get(url, { responseType: 'blob' })
    modalImageUrl.value = URL.createObjectURL(response.data)
  } catch (error) {
    console.error('Error fetching full image:', error)
    modal.error('Could not load full image.')
  }
}

// --- FILE UPLOAD LOGIC ---
const handleFileSelect = (e) => uploadFiles(e.target.files)
const handleFileDrop = (e) => uploadFiles(e.dataTransfer.files)

const uploadFiles = async (fileList) => {
  if (!fileList || fileList.length === 0) return
  uploading.value = true
  const testId = route.params.testId
  const filesToUpload = Array.from(fileList)

  for (let i = 0; i < filesToUpload.length; i++) {
    uploadProgress.value = `(${i + 1}/${filesToUpload.length})`
    const file = filesToUpload[i]
    const formData = new FormData()
    formData.append('file', file)
    try {
      await axios.post(`/tests/${testId}/answers/pages`, formData)
    } catch (error) { console.error(`Upload error for ${file.name}:`, error) }
  }
  uploading.value = false
  uploadProgress.value = ''
  fetchPages()
}

// --- NAVIGATION LOGIC ---
const goToText = () => router.push({ name: 'AnswersText', params: { testId: route.params.testId }, query: route.query })
const goToQR = () => router.push({ name: 'AnswersQR', params: { testId: route.params.testId }, query: route.query })
const goBack = () => router.go(-1)

const triggerNext = async () => {
  navLoading.value = true
  const testId = route.params.testId
  try {
    await axios.post(`/tests/${testId}/instruction/generate`, {}, {
      timeout: 180000
    })
    router.push({ name: 'InstructionsEditor', params: { testId } })
  } catch (error) {
    if (error.code === 'ECONNABORTED') {
      modal.error('Instruction generation timed out.')
    } else {
      modal.error('An error occurred while generating instructions.')
    }
  } finally {
    navLoading.value = false
  }
}

// --- WEBSOCKET LOGIC ---
const setupWebSocket = () => {
  const testId = route.params.testId
  const socket = new SockJS(process.env.VUE_APP_WS_URL)
  stompClient.value = Stomp.over(socket)
  stompClient.value.debug = null

  stompClient.value.connect({}, () => {
    const topic = `/topic/tests/${testId}/status`
    stompClient.value.subscribe(topic, (message) => {
      const body = JSON.parse(message.body)
      if (body.type === 'answer_page_uploaded' || body.type === 'answer_page_deleted') {
        fetchPages()
      }
    })
  }, (error) => { console.error('WebSocket connection error:', error) })
}

const disconnectWebSocket = () => {
  if (stompClient.value) {
    stompClient.value.disconnect(() => {})
  }
}

// --- LIFECYCLE HOOKS ---
onMounted(() => {
  fetchPages()
  setupWebSocket()
})

onBeforeUnmount(() => {
  disconnectWebSocket()
  Object.values(thumbnailUrls.value).forEach(url => URL.revokeObjectURL(url))
  if (modalImageUrl.value) {
    URL.revokeObjectURL(modalImageUrl.value)
  }
})
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>
